import { useEffect, useRef, type MutableRefObject } from "react";

type RecordingState = "inactive" | "recording" | "paused";

type AudioVisualizerParams = {
  recordingState: RecordingState;
  recordingStateRef: MutableRefObject<RecordingState>;
  analyserRef: MutableRefObject<AnalyserNode | null>;
  dataArrayRef: MutableRefObject<Uint8Array | null>;
  volumeBarRef: MutableRefObject<HTMLCanvasElement | null>;
};

const useAudioVisualizer = ({
  recordingState,
  recordingStateRef,
  analyserRef,
  dataArrayRef,
  volumeBarRef,
}: AudioVisualizerParams) => {
  const smoothedBarsRef = useRef<number[]>([]);

  useEffect(() => {
    if (recordingState !== "recording") return;

    const canvas = volumeBarRef.current;
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    if (!canvas || !analyser || !dataArray) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const barWidth = 8;
    const barGap = 3;
    const barRadius = 3;
    const maxBarHeight = height * 0.9;
    const minBarHeight = 3;
    const gain = 50;
    const smoothing = 0.15;
    const releaseSmoothing = 0.82;
    const noiseFloor = 0.005;
    const barCount = Math.max(1, Math.floor(width / (barWidth + barGap)));
    const totalBarsWidth = barCount * (barWidth + barGap) - barGap;
    const startX = (width - totalBarsWidth) / 2;

    if (smoothedBarsRef.current.length !== barCount) {
      smoothedBarsRef.current = new Array(barCount).fill(0);
    }

    type RoundRectContext = CanvasRenderingContext2D & {
      roundRect?: (x: number, y: number, w: number, h: number, r: number | number[]) => void;
    };

    const drawRoundedRect = (
      context: RoundRectContext,
      x: number,
      y: number,
      w: number,
      h: number,
      r: number
    ) => {
      const radius = Math.min(r, w / 2, h / 2);
      if (typeof context.roundRect === "function") {
        context.beginPath();
        context.roundRect(x, y, w, h, radius);
        context.fill();
        return;
      }
      context.beginPath();
      context.moveTo(x + radius, y);
      context.lineTo(x + w - radius, y);
      context.quadraticCurveTo(x + w, y, x + w, y + radius);
      context.lineTo(x + w, y + h - radius);
      context.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      context.lineTo(x + radius, y + h);
      context.quadraticCurveTo(x, y + h, x, y + h - radius);
      context.lineTo(x, y + radius);
      context.quadraticCurveTo(x, y, x + radius, y);
      context.closePath();
      context.fill();
    };

    const monitor = () => {
      if (recordingStateRef.current === "paused") {
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
        ctx.fillRect(0, 0, width, height);

        ctx.fillStyle = "#666";
        const pauseBarWidth = 10;
        const pauseBarGap = 8;
        const pauseBarHeight = 30;
        const x = width / 2 - pauseBarWidth - pauseBarGap / 2;
        const y = height / 2 - pauseBarHeight / 2;
        ctx.fillRect(x, y, pauseBarWidth, pauseBarHeight);
        ctx.fillRect(x + pauseBarWidth + pauseBarGap, y, pauseBarWidth, pauseBarHeight);
        return;
      }

      analyser.getByteTimeDomainData(dataArray);

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#666666";

      const samplesPerBar = Math.max(1, Math.floor(dataArray.length / barCount));

      for (let i = 0; i < barCount; i++) {
        const start = i * samplesPerBar;
        let sum = 0;

        for (let j = 0; j < samplesPerBar && start + j < dataArray.length; j++) {
          const v = dataArray[start + j] / 128 - 1;
          sum += Math.abs(v);
        }

        const avg = sum / samplesPerBar;
        const adjusted = Math.max(0, avg - noiseFloor);
        const center = (barCount - 1) / 2;
        const denom = center || 1;
        const distance = Math.abs(i - center) / denom;
        const envelope = 0.05 + 0.95 * Math.pow(1 - distance, 1.2);
        const target = adjusted * maxBarHeight * gain * envelope;
        const prev = smoothedBarsRef.current[i] ?? 0;
        const smoothed = target > prev
          ? prev * smoothing + target * (1 - smoothing)
          : prev * releaseSmoothing + target * (1 - releaseSmoothing);
        smoothedBarsRef.current[i] = smoothed;
        const barHeight = Math.min(height * 0.95, Math.max(minBarHeight, smoothed));
        const x = startX + i * (barWidth + barGap);
        const y = (height - barHeight) / 2;

        drawRoundedRect(ctx as RoundRectContext, x, y, barWidth, barHeight, barRadius);
      }

      if (recordingStateRef.current === "recording") {
        requestAnimationFrame(monitor);
      }
    };

    monitor();
  }, [recordingState, analyserRef, dataArrayRef, volumeBarRef, recordingStateRef]);
};

export default useAudioVisualizer;
